"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

export type SensorType =
  | "temperature"
  | "humidity"
  | "pressure"
  | "light"
  | "electrical"
  | "voltage"
  | "current"
  | "power"
  | "co2"
  | "pm25"
  | "pm10"
  | "voc"
  | "noise"
  | "motion"
  | "occupancy"
  | "water"

export interface SensorData {
  id: string
  name: string
  type: SensorType
  value: number
  unit: string
  min: number
  max: number
  position: [number, number, number]
  status: "normal" | "warning" | "critical"
  lastUpdated: Date
  history: { timestamp: Date; value: number }[]
  accuracy: number // 0-100%
  batteryLevel?: number // 0-100%
  isOnline: boolean
  metadata?: Record<string, any>
}

interface DataContextType {
  sensors: SensorData[]
  selectedEnvironment: string
  setSelectedEnvironment: (env: string) => void
  isRealTime: boolean
  setIsRealTime: (realTime: boolean) => void
  updateSensorValue: (id: string, value: number) => void
  getSensorsByType: (type: SensorType) => SensorData[]
  getSensorsByStatus: (status: "normal" | "warning" | "critical") => SensorData[]
  getAverageByType: (type: SensorType) => number | null
}

const DataContext = createContext<DataContextType | undefined>(undefined)

export function DataProvider({ children }: { children: ReactNode }) {
  const [selectedEnvironment, setSelectedEnvironment] = useState("datacenter")
  const [isRealTime, setIsRealTime] = useState(true)
  const [sensors, setSensors] = useState<SensorData[]>([])

  // Initialize sensors based on environment
  useEffect(() => {
    const initializeSensors = () => {
      const environmentSensors: Record<string, Partial<SensorData>[]> = {
        datacenter: [
          { id: "temp1", name: "Server Rack A", type: "temperature", min: 15, max: 35, position: [2, 1, 2] },
          { id: "temp2", name: "Server Rack B", type: "temperature", min: 15, max: 35, position: [-2, 1, 2] },
          { id: "hum1", name: "Room Humidity", type: "humidity", min: 40, max: 60, position: [0, 2, 0] },
          { id: "power1", name: "Power Consumption", type: "power", min: 0, max: 1000, position: [3, 0.5, -1] },
          { id: "voltage1", name: "Voltage", type: "voltage", min: 220, max: 240, position: [3, 1.5, -1] },
          { id: "current1", name: "Current", type: "current", min: 0, max: 30, position: [3, 2.5, -1] },
          { id: "press1", name: "Air Pressure", type: "pressure", min: 1000, max: 1020, position: [0, 3, 0] },
          { id: "co2_1", name: "CO2 Level", type: "co2", min: 400, max: 1000, position: [1, 3, 1] },
        ],
        greenhouse: [
          { id: "temp1", name: "Air Temperature", type: "temperature", min: 18, max: 28, position: [1, 2, 1] },
          { id: "hum1", name: "Soil Humidity", type: "humidity", min: 60, max: 80, position: [-1, 0, 1] },
          { id: "light1", name: "Light Intensity", type: "light", min: 0, max: 100, position: [0, 3, 0] },
          { id: "temp2", name: "Soil Temperature", type: "temperature", min: 15, max: 25, position: [2, 0, -1] },
          { id: "co2_1", name: "CO2 Level", type: "co2", min: 400, max: 1200, position: [-2, 2, 0] },
          { id: "water1", name: "Water Level", type: "water", min: 0, max: 100, position: [0, 0, 2] },
        ],
        charging: [
          { id: "power1", name: "Charging Power", type: "power", min: 0, max: 350, position: [1, 1, 0] },
          { id: "temp1", name: "Battery Temperature", type: "temperature", min: 20, max: 45, position: [0, 1, 1] },
          { id: "voltage1", name: "Charging Voltage", type: "voltage", min: 380, max: 420, position: [1, 2, 0] },
          { id: "current1", name: "Charging Current", type: "current", min: 0, max: 125, position: [1, 3, 0] },
          { id: "power2", name: "Grid Load", type: "power", min: 0, max: 500, position: [-1, 1, 0] },
        ],
        smartbuilding: [
          { id: "temp1", name: "Living Room", type: "temperature", min: 18, max: 26, position: [2, 1, 2] },
          { id: "temp2", name: "Bedroom", type: "temperature", min: 18, max: 26, position: [-2, 1, 2] },
          { id: "hum1", name: "Indoor Humidity", type: "humidity", min: 40, max: 60, position: [0, 2, 0] },
          { id: "co2_1", name: "Air Quality", type: "co2", min: 400, max: 1000, position: [0, 3, 0] },
          { id: "power1", name: "Energy Usage", type: "power", min: 0, max: 5, position: [3, 0.5, -1] },
          { id: "light1", name: "Light Level", type: "light", min: 0, max: 100, position: [1, 2, 1] },
          { id: "motion1", name: "Motion Sensor", type: "motion", min: 0, max: 1, position: [-1, 1, 1] },
          { id: "occupancy1", name: "Occupancy", type: "occupancy", min: 0, max: 10, position: [0, 1, 0] },
        ],
        industrial: [
          { id: "temp1", name: "Machine 1 Temp", type: "temperature", min: 20, max: 80, position: [2, 1, 2] },
          { id: "temp2", name: "Machine 2 Temp", type: "temperature", min: 20, max: 80, position: [-2, 1, 2] },
          { id: "power1", name: "Power Usage", type: "power", min: 0, max: 2000, position: [3, 0.5, -1] },
          { id: "pressure1", name: "System Pressure", type: "pressure", min: 0, max: 100, position: [0, 2, 0] },
          { id: "noise1", name: "Noise Level", type: "noise", min: 0, max: 120, position: [1, 3, 1] },
          { id: "voc1", name: "VOC Level", type: "voc", min: 0, max: 500, position: [-1, 2, 1] },
          { id: "pm25_1", name: "PM2.5", type: "pm25", min: 0, max: 50, position: [0, 3, 0] },
          { id: "vibration1", name: "Vibration", type: "motion", min: 0, max: 100, position: [2, 0, -1] },
        ],
      }

      const baseSensors = environmentSensors[selectedEnvironment] || environmentSensors.datacenter

      setSensors(
        baseSensors.map((sensor) => ({
          id: sensor.id || `sensor_${Math.random().toString(36).substring(2, 9)}`,
          name: sensor.name || `Sensor`,
          type: sensor.type || "temperature",
          value: getRandomValueInRange(sensor.min || 0, sensor.max || 100),
          unit: getUnit(sensor.type || "temperature"),
          min: sensor.min || 0,
          max: sensor.max || 100,
          position: sensor.position || [0, 0, 0],
          status: "normal" as const,
          lastUpdated: new Date(),
          history: Array.from({ length: 20 }, (_, i) => ({
            timestamp: new Date(Date.now() - (19 - i) * 60000),
            value: getRandomValueInRange(sensor.min || 0, sensor.max || 100),
          })),
          accuracy: 95 + Math.random() * 5, // 95-100%
          batteryLevel: Math.round(70 + Math.random() * 30), // 70-100%
          isOnline: true,
          metadata: {},
        })),
      )
    }

    initializeSensors()
  }, [selectedEnvironment])

  // Simulate real-time data updates with data validation
  useEffect(() => {
    if (!isRealTime) return

    const interval = setInterval(() => {
      setSensors((prev) =>
        prev.map((sensor) => {
          // Simulate occasional sensor going offline
          const isOnline = Math.random() > 0.01 ? true : false

          if (!isOnline) {
            return { ...sensor, isOnline, status: "critical" }
          }

          // Calculate new value with some randomness but also continuity from previous value
          const range = sensor.max - sensor.min
          const previousValue = sensor.value
          const maxChange = range * 0.05 // Max 5% change per update
          const rawChange = (Math.random() - 0.5) * maxChange

          // Apply some continuity - tend to continue in same direction
          const lastChange =
            sensor.history.length > 1
              ? sensor.history[sensor.history.length - 1].value - sensor.history[sensor.history.length - 2].value
              : 0
          const directionBias = Math.sign(lastChange) * 0.3 // 30% bias to continue trend
          const change = rawChange + Math.abs(rawChange) * directionBias

          let newValue = previousValue + change

          // Data validation - ensure within absolute bounds
          newValue = Math.max(sensor.min, Math.min(sensor.max, newValue))

          // Round to appropriate precision based on sensor type
          const precision = getPrecision(sensor.type)
          newValue = Math.round(newValue * Math.pow(10, precision)) / Math.pow(10, precision)

          // Determine status based on value and thresholds
          let status: "normal" | "warning" | "critical" = "normal"
          const warningThreshold = 0.15 // 15% from min/max
          const criticalThreshold = 0.05 // 5% from min/max

          const minWarning = sensor.min + range * warningThreshold
          const maxWarning = sensor.max - range * warningThreshold
          const minCritical = sensor.min + range * criticalThreshold
          const maxCritical = sensor.max - range * criticalThreshold

          if (newValue <= minCritical || newValue >= maxCritical) {
            status = "critical"
          } else if (newValue <= minWarning || newValue >= maxWarning) {
            status = "warning"
          }

          // Update history
          const newHistory = [...sensor.history.slice(-19), { timestamp: new Date(), value: newValue }]

          return {
            ...sensor,
            value: newValue,
            status,
            lastUpdated: new Date(),
            history: newHistory,
            isOnline,
            // Simulate battery drain
            batteryLevel: sensor.batteryLevel ? Math.max(0, sensor.batteryLevel - Math.random() * 0.1) : undefined,
          }
        }),
      )
    }, 2000)

    return () => clearInterval(interval)
  }, [isRealTime])

  // Function to update a sensor value manually
  const updateSensorValue = (id: string, value: number) => {
    setSensors((prev) =>
      prev.map((sensor) => {
        if (sensor.id === id) {
          // Validate the value
          const validValue = Math.max(sensor.min, Math.min(sensor.max, value))

          // Determine status
          const range = sensor.max - sensor.min
          let status: "normal" | "warning" | "critical" = "normal"
          const warningThreshold = 0.15
          const criticalThreshold = 0.05

          const minWarning = sensor.min + range * warningThreshold
          const maxWarning = sensor.max - range * warningThreshold
          const minCritical = sensor.min + range * criticalThreshold
          const maxCritical = sensor.max - range * criticalThreshold

          if (validValue <= minCritical || validValue >= maxCritical) {
            status = "critical"
          } else if (validValue <= minWarning || validValue >= maxWarning) {
            status = "warning"
          }

          // Update history
          const newHistory = [...sensor.history.slice(-19), { timestamp: new Date(), value: validValue }]

          return {
            ...sensor,
            value: validValue,
            status,
            lastUpdated: new Date(),
            history: newHistory,
          }
        }
        return sensor
      }),
    )
  }

  // Function to get sensors by type
  const getSensorsByType = (type: SensorType) => {
    return sensors.filter((sensor) => sensor.type === type)
  }

  // Function to get sensors by status
  const getSensorsByStatus = (status: "normal" | "warning" | "critical") => {
    return sensors.filter((sensor) => sensor.status === status)
  }

  // Function to get average value by sensor type
  const getAverageByType = (type: SensorType) => {
    const typeSensors = getSensorsByType(type)
    if (typeSensors.length === 0) return null

    const sum = typeSensors.reduce((acc, sensor) => acc + sensor.value, 0)
    return sum / typeSensors.length
  }

  return (
    <DataContext.Provider
      value={{
        sensors,
        selectedEnvironment,
        setSelectedEnvironment,
        isRealTime,
        setIsRealTime,
        updateSensorValue,
        getSensorsByType,
        getSensorsByStatus,
        getAverageByType,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

// Helper functions
function getUnit(type: SensorType): string {
  switch (type) {
    case "temperature":
      return "°C"
    case "humidity":
      return "%"
    case "pressure":
      return "hPa"
    case "light":
      return "lux"
    case "power":
      return "kW"
    case "voltage":
      return "V"
    case "current":
      return "A"
    case "co2":
      return "ppm"
    case "pm25":
      return "μg/m³"
    case "pm10":
      return "μg/m³"
    case "voc":
      return "ppb"
    case "noise":
      return "dB"
    case "motion":
      return "%"
    case "occupancy":
      return "people"
    case "water":
      return "%"
    default:
      return ""
  }
}

function getPrecision(type: SensorType): number {
  switch (type) {
    case "temperature":
      return 1
    case "humidity":
      return 1
    case "pressure":
      return 0
    case "voltage":
      return 1
    case "current":
      return 2
    case "power":
      return 2
    case "co2":
      return 0
    case "pm25":
    case "pm10":
      return 1
    default:
      return 1
  }
}

function getRandomValueInRange(min: number, max: number): number {
  // Generate a value that tends to be in the middle of the range
  const range = max - min
  const midpoint = min + range / 2
  const deviation = range / 4

  // Normal-ish distribution around midpoint
  const value = midpoint + (Math.random() - 0.5) * 2 * deviation

  // Ensure within bounds
  return Math.max(min, Math.min(max, value))
}

export function useData() {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error("useData must be used within DataProvider")
  }
  return context
}
